import React, { useEffect, useState } from 'react';
import Loader from "../../components/Loader"; 
import Paginator from "../../components/Paginator"; 
import ApiService from "../../services/ApiService";
import { Link } from 'react-router-dom';
import { formatDate, formatCurrency } from '../../utils/helper';
import { toast } from 'react-toastify';
import { getAppointmentStatus } from '../../utils/statusHelper';
import AuthService from '../../services/AuthService';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import { Dropdown } from 'primereact/dropdown';

const List = () => {
    const [isLoader, setIsLoader] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [pagination, setPagination] = useState({});
    const authUser = AuthService.getCurrentUser();
    const isCustomer = (authUser?.role === 'customer');
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [branches, setBranches] = useState([]);
    const [customers, setCustomers] = useState([]);
    
    const [apiData, setApiData] = useState({
        search: "",
        page: 1,
        start_date: "",
        end_date: "",
        status: "",
        branch_id: "",
        customer_id: ""
    });
    const [activeTab, setActiveTab] = useState('upcoming');

    useEffect(() => {
        getAppointments();
        if (!isCustomer) {
            getBranches();
            getCustomers();
        }
    }, [apiData.page, apiData.search, apiData.start_date, apiData.end_date, apiData.status, apiData.branch_id, apiData.customer_id]);

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setApiData(prev => ({
            ...prev,
            start_date: start ? formatDate(start, 'YYYY-MM-DD') : "",
            end_date: end ? formatDate(end, 'YYYY-MM-DD') : ""
        }));
    }; 

    const getBranches = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: '/admin/branches',
                data: { per_page: 1000 }
            });
            if (response.data.status) {
                setBranches(response.data.data.branches || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getCustomers = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: '/admin/customers',
                data: { per_page: 1000 }
            });
            if (response.data.status) {
                const formatted = (response.data.data.customers || []).map(p => ({
                    ...p,
                    label: `${p.name} (${p.phone})`
                }));
                setCustomers(formatted);
            }
        } catch (error) {
            console.error(error);
        }
    }; 

    const getAppointments = async () => {
        try {
            setIsLoader(true);
            const isCustomer = authUser?.role === 'customer';
            const url = isCustomer ? 'customer/appointments' : 'admin/appointments';
            const response = await ApiService.request({
                method: "GET",
                url: url, 
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setAppointments(data.data.appointments || []); 
                setPagination(data.data.pagination || {});
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoader(false);
        }
    };

    const deleteAppointment = async (id) => {
        if (!window.confirm("Are you sure you want to delete this appointment?")) return;
        
        try {
            setIsLoader(true);
            const isCustomer = authUser?.role === 'customer';
            const url = isCustomer ? `customer/appointments/${id}` : `admin/appointments/${id}`; // Note: customer delete might not be allowed by backend
            // Ideally hide delete button for customer if not allowed
            
            const response = await ApiService.request({
                method: "DELETE",
                url: url,
            });
            if (response.data.status) {
                toast.success(response.data.message);
                getAppointments();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setIsLoader(false);
        }
    };

    const moveAppointment = async (id, direction) => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "POST",
                url: `admin/appointments/${id}/move`,
                data: { direction }
            });
            if (response.data.status) {
                // toast.success(response.data.message);
                getAppointments();
            } else {
                // toast.error(response.data.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoader(false);
        }
    };

    const cancelAppointment = async (id) => {
        if (!window.confirm("Are you sure you want to cancel this appointment?")) return;
        
        try {
            setIsLoader(true);
            const isCustomer = authUser?.role === 'customer';
            const url = isCustomer ? `customer/appointments/${id}/cancel` : `admin/appointments/${id}/cancel`; 
            
            const response = await ApiService.request({
                method: "POST",
                url: url,
            });
            if (response.data.status) {
                toast.success(response.data.message);
                getAppointments();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setIsLoader(false);
        }
    };

    const deleteItem = async (id) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "Do you want to cancel this appointment?",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, cancel it!'
            });

            if (result.isConfirmed) {
                const isCustomer = authUser?.role === 'customer';
                const url = isCustomer ? `customer/appointments/${id}/cancel` : `admin/appointments/${id}/cancel`;
                
                const response = await ApiService.request({
                    method: 'POST',
                    url: url
                });

                if (response.data.status) {
                    Swal.fire(
                        'Cancelled!',
                        'Appointment has been cancelled.',
                        'success'
                    );
                    getAppointments();
                } else {
                    Swal.fire(
                        'Error!',
                        response.data.message || 'Something went wrong',
                        'error'
                    );
                }
            }
        } catch (error) {
            console.error('Error cancelling appointment:', error);
            Swal.fire(
                'Error!',
                'Failed to cancel appointment',
                'error'
            );
        }
    };

    if (isCustomer) {
        // Customer Grid View with Tabs
        const upcomingAppointments = appointments.filter(a => ['pending', 'confirmed'].includes(a.status));
        const pastAppointments = appointments.filter(a => ['completed', 'cancelled'].includes(a.status));
        
        return (
            <div className="container py-5">
                <div className="d-flex justify-content-between align-items-center mb-5">
                    <h2 className="fw-bolder text-dark">My Appointments</h2>
                    <Link to="/appointments/create" className="btn btn-base">
                        <i className="fas fa-plus me-2"></i> Book New
                    </Link>
                </div>

                <ul className="nav nav-tabs nav-line-tabs mb-5 fs-6">
                    <li className="nav-item">
                        <a 
                            className={`nav-link ${activeTab === 'upcoming' ? 'active' : ''}`} 
                            href="#"
                            onClick={(e) => { e.preventDefault(); setActiveTab('upcoming'); }}
                        >
                            Upcoming
                        </a>
                    </li>
                    <li className="nav-item">
                        <a 
                            className={`nav-link ${activeTab === 'past' ? 'active' : ''}`} 
                            href="#"
                            onClick={(e) => { e.preventDefault(); setActiveTab('past'); }}
                        >
                            Past
                        </a>
                    </li>
                </ul>

                <div className="tab-content" id="myTabContent">
                    <div className={`tab-pane fade ${activeTab === 'upcoming' ? 'show active' : ''}`} role="tabpanel">
                        <div className="row g-5">
                            {upcomingAppointments.length > 0 ? upcomingAppointments.map(appointment => (
                                <div className="col-md-6 col-lg-4" key={appointment.id}>
                                    <div className="card h-100 border-hover-dark shadow-sm">
                                        <div className="card-header border-0 pt-5">
                                            <div className="card-title m-0">
                                                <div className="d-flex align-items-center">
                                                    <div className="symbol symbol-50px me-3">
                                                        <span className="symbol-label bg-light-primary text-base fs-2 fw-bold">
                                                            {new Date(appointment.date).getDate()}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex flex-column">
                                                        <span className="fs-6 fw-bold text-dark">{formatDate(appointment.date, "MMM YYYY")}</span>
                                                        <span className="fs-7 text-muted">{appointment.time}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="card-toolbar">
                                                {(() => {
                                                    const { badgeClass, label } = getAppointmentStatus(appointment.status);
                                                    return (
                                                        <span className={`badge ${badgeClass} fw-bold`}>
                                                            {label}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            <div className="d-flex flex-column mb-5">
                                                <span className="text-gray-400 fw-semibold fs-7">Service</span>
                                                <span className="text-dark fw-bold fs-5">
                                                    {appointment.services && appointment.services.length > 0
                                                        ? appointment.services.map(s => s.name).join(', ')
                                                        : (appointment.service?.name || 'Multiple Services')}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex flex-column">
                                                    <span className="text-gray-400 fw-semibold fs-7">Price</span>
                                                    <span className="text-base fw-bold fs-4">{formatCurrency(appointment.price)}</span>
                                                </div>
                                                <div className="d-flex flex-column text-end">
                                                    <span className="text-gray-400 fw-semibold fs-7">Ref No</span>
                                                    <span className="text-dark fw-bold">{appointment.ref_no || '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-footer d-flex justify-content-end py-3">
                                            {appointment.status === 'pending' && (
                                                <button onClick={() => deleteItem(appointment.id)} className="btn btn-sm btn-light-danger">
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="col-12 text-center py-10">
                                    <div className="text-muted fs-5">No upcoming appointments found</div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`tab-pane fade ${activeTab === 'past' ? 'show active' : ''}`} role="tabpanel">
                         <div className="row g-5">
                            {pastAppointments.length > 0 ? pastAppointments.map(appointment => (
                                <div className="col-md-6 col-lg-4" key={appointment.id}>
                                    <div className="card h-100 border bg-white shadow-none">
                                        <div className="card-header border-0 pt-5">
                                            <div className="card-title m-0">
                                                <div className="d-flex align-items-center">
                                                    <div className="symbol symbol-50px me-3">
                                                        <span className="symbol-label bg-white text-muted fs-2 fw-bold">
                                                            {new Date(appointment.date).getDate()}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex flex-column">
                                                        <span className="fs-6 fw-bold text-dark">{formatDate(appointment.date, "MMM YYYY")}</span>
                                                        <span className="fs-7 text-muted">{appointment.time}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="card-toolbar">
                                                {(() => {
                                                    const { badgeClass, label } = getAppointmentStatus(appointment.status);
                                                    return (
                                                        <span className={`badge ${badgeClass} fw-bold`}>
                                                            {label}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            <div className="d-flex flex-column mb-5">
                                                <span className="text-gray-400 fw-semibold fs-7">Service</span>
                                                <span className="text-dark fw-bold fs-5">
                                                    {appointment.services && appointment.services.length > 0
                                                        ? appointment.services.map(s => s.name).join(', ')
                                                        : (appointment.service?.name || 'Multiple Services')}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex flex-column">
                                                    <span className="text-gray-400 fw-semibold fs-7">Price</span>
                                                    <span className="text-dark fw-bold fs-4">{formatCurrency(appointment.price)}</span>
                                                </div>
                                                <div className="d-flex flex-column text-end">
                                                    <span className="text-gray-400 fw-semibold fs-7">Ref No</span>
                                                    <span className="text-dark fw-bold">{appointment.ref_no || '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="col-12 text-center py-10">
                                    <div className="text-muted fs-5">No past appointments found</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Appointments
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/appointments/create" className="btn btn-sm fw-bold btn-base">
                            Create Appointment
                        </Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                         <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <i className="fas fa-search fs-1"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search Appointment"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-3">
                                    {!isCustomer && (
                                        <>
                                            <div className="w-200px">
                                                <select 
                                                    className="form-select form-select-solid"
                                                    value={apiData.branch_id}
                                                    onChange={(e) => setApiData({...apiData, branch_id: e.target.value})}
                                                >
                                                    <option value="">All Branches</option>
                                                    {branches.map(branch => (
                                                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="w-200px">
                                                <Dropdown
                                                    value={apiData.customer_id}
                                                    options={customers}
                                                    onChange={(e) => setApiData({ ...apiData, customer_id: e.value })}
                                                    optionLabel="label"
                                                    optionValue="id"
                                                    filter
                                                    placeholder="All Customers"
                                                    className="w-100"
                                                    showClear
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div className="w-200px">
                                        <select 
                                            className="form-select form-select-solid"
                                            value={apiData.status}
                                            onChange={(e) => setApiData({...apiData, status: e.target.value})}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="pending">Pending</option>
                                            <option value="confirmed">Confirmed</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div className="w-200px">
                                         <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid"
                                            placeholderText="Select Date Range"
                                            isClearable={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            {isLoader ? (
                                <Loader />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th className="min-w-100px">Ref No</th>
                                                {!isCustomer && <th className="min-w-125px">Customer</th>}
                                                <th className="min-w-125px">Branch</th>
                                                <th className="min-w-125px">Service</th>
                                                <th className="min-w-125px">Total Amount</th>
                                                <th className="min-w-125px">Date & Time</th>
                                                <th className="min-w-100px">Status</th>
                                                <th className="text-end min-w-100px">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {appointments.map((item) => (
                                                <tr key={item.id}>
                                                    <td>{item.ref_no || '-'}</td>
                                                    {!isCustomer && (
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span className="text-gray-800 mb-1">{item.customer?.name}</span>
                                                                <span className="text-muted fs-7">{item.customer?.phone}</span>
                                                            </div>
                                                        </td>
                                                    )}
                                                    <td>{item.branch?.name}</td>
                                                    <td>
                                                        {item.services && item.services.length > 0
                                                            ? item.services.map(s => s.name).join(', ')
                                                            : (item.service?.name || '-')}
                                                    </td>
                                                    <td>
                                                        <span className="text-gray-800 mb-1">{formatCurrency(item.total_amount)}</span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            <span className="text-gray-800 mb-1">{formatDate(item.date)}</span>
                                                            <span className="text-muted fs-7">{item.time}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {(() => {
                                                            const { badgeClass, label } = getAppointmentStatus(item.status);
                                                            return (
                                                                <span className={`badge ${badgeClass}`}>
                                                                    {label}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="text-end">
                                                        <Link 
                                                            to={`/appointments/${item.id}`}
                                                            className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                                            title="View"
                                                        >
                                                            <i className="fas fa-eye fs-3"></i>
                                                        </Link>
                                                        <Link 
                                                            to={`/appointments/edit/${item.id}`}
                                                            className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                                            title="Edit"
                                                        >
                                                            <i className="fas fa-edit fs-3"></i>
                                                        </Link>
                                                        {isCustomer && ['pending', 'confirmed'].includes(item.status) && (
                                                            <button 
                                                                onClick={() => cancelAppointment(item.id)}
                                                                className="btn btn-icon btn-bg-light btn-active-color-warning btn-sm me-1"
                                                                title="Cancel Appointment"
                                                            >
                                                                <i className="fas fa-ban fs-3"></i>
                                                            </button>
                                                        )}
                                                        {!isCustomer && (
                                                            <button 
                                                                onClick={() => deleteAppointment(item.id)}
                                                                className="btn btn-icon btn-bg-light btn-active-color-danger btn-sm"
                                                                title="Delete"
                                                            >
                                                                <i className="fas fa-trash fs-3"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {appointments.length === 0 && (
                                                <tr>
                                                    <td colSpan="7" className="text-center">No appointments found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <Paginator pagination={pagination} onPageChange={(page) => setApiData({ ...apiData, page })} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
